import React from 'react';
import { withRouter } from 'react-router-dom';
import { Avatar, Button, Card, CardContent, Typography, Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import CampaignLogo from '../../../assets/campaign-logo.svg';
import { formatCount, formatINR, formatDate, titleCase } from '../../../utils/formatters';

const ACCENT = '#111936';

// How the creator's own invoice reads on the card once one exists.
const INVOICE_LABEL = {
  submitted: 'submitted',
  accepted: 'accepted',
  approved: 'approved',
  sent_for_payment: 'sent for payment',
  paid: 'paid',
  rejected: 'rejected',
};

const STATUS_STYLE = {
  live: { label: 'Live', color: '#7C3AED', background: '#F3E8FF', dot: true },
  running: { label: 'Running', color: '#7C3AED', background: '#F3E8FF', dot: true },
  completed: { label: 'Completed', color: '#15803D', background: '#DCFCE7' },
  paused: { label: 'Paused', color: '#92400E', background: '#FEF3C7' },
  draft: { label: 'Draft', color: '#4B5563', background: '#F3F4F6' },
};

const styles = () => ({
  card: {
    marginBottom: 16,
    borderRadius: 12,
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    cursor: 'pointer',
    transition: 'box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(16, 24, 40, 0.10)',
      transform: 'translateY(-2px)',
      borderColor: '#D1D5DB',
    },
    '&:focus-visible': {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 2,
    },
  },
  name: {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '22px',
    color: '#111827',
  },
  subtitle: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13,
    lineHeight: '18px',
    color: '#6B7280',
    marginTop: 2,
  },
  description: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13,
    lineHeight: '20px',
    color: '#4B5563',
    marginTop: 12,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    padding: '4px 12px',
    fontFamily: "'Lato', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  '@keyframes livePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(124, 58, 237, 0.45)' },
    '70%': { boxShadow: '0 0 0 5px rgba(124, 58, 237, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(124, 58, 237, 0)' },
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#7C3AED',
    animation: '$livePulse 1.6s ease-out infinite',
  },
  statsRow: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: '1px solid #F3F4F6',
  },
  statValue: {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 15,
    color: '#111827',
    lineHeight: '20px',
  },
  statLabel: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 11.5,
    color: '#9CA3AF',
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  footer: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: '1px solid #F3F4F6',
  },
  dateText: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 12.5,
    color: '#6B7280',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  brandAvatar: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: '1px solid #E5E7EB',
    background: '#F9FAFB',
  },
  invoiceButton: {
    textTransform: 'none',
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 13,
    color: 'white',
    background: ACCENT,
    borderRadius: 8,
    padding: '5px 14px',
    '&:hover': { background: '#177E91' },
  },
  viewLink: {
    textTransform: 'none',
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 13,
    color: ACCENT,
  },
});

function Stat({ classes, value, label, hidden }) {
  return (
    <Grid item xs={3}>
      <Typography className={classes.statValue}>{hidden ? '-' : value}</Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
    </Grid>
  );
}

class Campaign extends React.Component {
  state = { logoBroken: false };

  openFeed = () => {
    this.props.history.push(
      `${this.props.match.path}/${this.props.id}/feeds/${
        JSON.parse(localStorage.getItem('user')).influencer.id
      }/youtube/`,
    );
  };

  render() {
    const { classes } = this.props;
    const statusKey = this.props.is_live ? 'live' : this.props.status;
    const status = STATUS_STYLE[statusKey] || {
      label: titleCase(this.props.status),
      color: '#4B5563',
      background: '#F3F4F6',
    };
    const started = this.props.is_live || this.props.status !== 'draft';
    const brandName = this.props.brand_name;

    return (
      <Card
        elevation={0}
        className={classes.card}
        role="button"
        tabIndex={0}
        onClick={this.openFeed}
        onKeyDown={(event) => {
          if (event.key === 'Enter') this.openFeed();
        }}
      >
        <CardContent style={{ padding: '18px 20px' }}>
          <Grid container justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Grid item xs>
              <Grid container spacing={2} alignItems="center" wrap="nowrap">
                <Grid item>
                  <Avatar
                    variant="rounded"
                    src={
                      this.state.logoBroken || !this.props.brand_logo_url
                        ? CampaignLogo
                        : this.props.brand_logo_url
                    }
                    imgProps={{
                      style: { objectFit: 'contain' },
                      // A logo URL we hold but S3 no longer serves used to
                      // render as an empty box; fall back to the placeholder.
                      onError: () => this.setState({ logoBroken: true }),
                    }}
                    alt={brandName || 'brand logo'}
                    className={classes.brandAvatar}
                  />
                </Grid>
                <Grid item xs>
                  <Typography className={`${classes.name} campaign-name`}>
                    {this.props.name}
                  </Typography>
                  <Typography className={`${classes.subtitle} campaign-objective`}>
                    {brandName ? `${brandName}` : ''}
                    {brandName && this.props.objective ? ' · ' : ''}
                    {this.props.objective || ''}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <span
                className={classes.statusChip}
                style={{ color: status.color, background: status.background }}
              >
                {status.dot ? <span className={classes.liveDot} /> : null}
                {status.label}
              </span>
            </Grid>
          </Grid>

          {this.props.desc ? (
            <Typography className={`${classes.description} campaign-description`}>
              {this.props.desc}
            </Typography>
          ) : null}

          <Grid container className={classes.statsRow}>
            <Stat
              classes={classes}
              value={formatINR(this.props.budget)}
              label="Your payout"
            />
            <Stat
              classes={classes}
              value={formatCount(this.props.total_posts)}
              label="Posts"
              hidden={!started}
            />
            <Stat
              classes={classes}
              value={formatCount(this.props.total_views)}
              label="Views"
              hidden={!started}
            />
            <Stat
              classes={classes}
              value={formatCount(this.props.total_engagement_rate)}
              label="Engagement"
              hidden={!started}
            />
          </Grid>

          <Grid
            container
            className={classes.footer}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              <Typography component="span" className={classes.dateText}>
                <CalendarTodayOutlinedIcon style={{ fontSize: 14 }} />
                {formatDate(this.props.start_date)} to {formatDate(this.props.end_date)}
                {this.props.duration ? ` · ${this.props.duration} days` : ''}
              </Typography>
            </Grid>
            <Grid item>
              {this.props.can_raise_invoice ? (
                <Button
                  size="small"
                  className={classes.invoiceButton}
                  onClick={(event) => {
                    // The whole card is a link to the campaign feed.
                    event.stopPropagation();
                    this.props.history.push('/influencer/invoices');
                  }}
                >
                  Raise invoice
                </Button>
              ) : this.props.invoice_status ? (
                <Button
                  size="small"
                  className={classes.viewLink}
                  endIcon={<ArrowForwardIcon style={{ fontSize: 15 }} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    this.props.history.push('/influencer/invoices');
                  }}
                >
                  Invoice {INVOICE_LABEL[this.props.invoice_status] || this.props.invoice_status}
                </Button>
              ) : (
                <Button
                  size="small"
                  className={classes.viewLink}
                  endIcon={<ArrowForwardIcon style={{ fontSize: 15 }} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    this.openFeed();
                  }}
                >
                  View posts
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }
}

export default withRouter(withStyles(styles)(Campaign));
